import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { queryKeys } from 'constants/queryKeys';
import {
  fetchMyNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from 'shared/data/repositories/notificationsRepository';
import {
  useAnnouncementInbox,
  useMarkAnnouncementRead,
} from 'features/announcements/hooks/useAnnouncements';
import type { AnnouncementInboxItem } from 'shared/data/repositories/announcementRepository';
import type { Notification } from 'types';
import { cn } from 'utils/cn';

type BellItem =
  | {
      kind: 'notification';
      id: string;
      title: string;
      message: string;
      meta?: string;
      isRead: boolean;
      createdAt: string;
      link?: string;
      sortAt: number;
    }
  | {
      kind: 'announcement';
      id: string;
      title: string;
      message: string;
      meta: string;
      isRead: boolean;
      createdAt: string;
      link?: string;
      sortAt: number;
      announcementId: string;
    };

const VARIANT_LABEL: Record<string, string> = {
  info: 'Info',
  promo: 'Promotion',
  urgent: 'Important',
  success: 'Info',
};

function announcementMeta(item: AnnouncementInboxItem): string {
  const label = VARIANT_LABEL[item.variant] ?? 'Announcement';
  if (item.scope === 'global') return `${label} · Platform`;
  if (item.scope === 'batch') return `${label} · Batch`;
  return label;
}

const NOTIFICATION_TYPE_LABEL: Record<string, string> = {
  request: 'Request',
  mention: 'Mention',
  assignment: 'Assignment',
  live: 'Live',
  success: 'Info',
  warning: 'Alert',
  info: 'Info',
  path_root_question: 'Question',
  path_discussion_reply: 'Reply',
};

function notificationMeta(type: string): string {
  return NOTIFICATION_TYPE_LABEL[type] ?? 'Update';
}

function isExternalUrl(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

export function NotificationsBell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const { data: notifications = [] } = useQuery({
    queryKey: queryKeys.notifications.all,
    queryFn: () => fetchMyNotifications(20),
    refetchInterval: 60_000,
  });

  const { data: announcements = [] } = useAnnouncementInbox();
  const markAnnouncement = useMarkAnnouncementRead();

  const items = useMemo<BellItem[]>(() => {
    const notifItems: BellItem[] = notifications.map((n: Notification) => ({
      kind: 'notification' as const,
      id: `n-${n.id}`,
      title: n.title,
      message: n.message,
      meta: notificationMeta(n.type),
      isRead: n.isRead,
      createdAt: n.createdAt,
      link: n.link,
      sortAt: new Date(n.createdAt).getTime() || 0,
    }));

    const annItems: BellItem[] = announcements.map((a) => ({
      kind: 'announcement' as const,
      id: `a-${a.id}`,
      title: a.title,
      message: a.shortBody,
      meta: announcementMeta(a),
      isRead: a.isRead,
      createdAt: a.startsAt || a.createdAt,
      link: a.ctaUrl ?? undefined,
      sortAt: new Date(a.startsAt || a.createdAt).getTime() || 0,
      announcementId: a.id,
    }));

    return [...annItems, ...notifItems].sort((a, b) => {
      if (a.isRead !== b.isRead) return a.isRead ? 1 : -1;
      return b.sortAt - a.sortAt;
    });
  }, [notifications, announcements]);

  const unread =
    notifications.filter((n) => !n.isRead).length +
    announcements.filter((a) => !a.isRead).length;

  const markOne = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });

  const markAll = useMutation({
    mutationFn: async () => {
      await markAllNotificationsRead();
      const unreadAnn = announcements.filter((a) => !a.isRead);
      await Promise.all(unreadAnn.map((a) => markAnnouncementReadQuiet(a.id)));
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
      await queryClient.invalidateQueries({ queryKey: ['announcements', 'inbox'] });
    },
  });

  useEffect(() => {
    if (!open) return;
    const onDoc = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const onOpenItem = async (item: BellItem) => {
    if (item.kind === 'notification') {
      const rawId = item.id.replace(/^n-/, '');
      if (!item.isRead) await markOne.mutateAsync(rawId);
      setOpen(false);
      if (item.link) navigate(item.link);
      return;
    }

    if (!item.isRead) {
      await markAnnouncement.mutateAsync(item.announcementId);
    }
    setOpen(false);
    if (item.link) {
      if (isExternalUrl(item.link)) {
        window.open(item.link, '_blank', 'noopener,noreferrer');
      } else {
        navigate(item.link);
      }
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-expanded={open}
        className="nav-icon-btn relative"
        onClick={() => setOpen((v) => !v)}
      >
        <Bell className="h-4 w-4" aria-hidden />
        {unread > 0 ? (
          <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-accent" aria-hidden />
        ) : null}
      </button>
      {open ? (
        <div className="absolute right-0 top-full z-50 mt-2 w-[min(100vw-2rem,22rem)] max-h-96 overflow-hidden rounded-2xl border border-border bg-surface shadow-modal sm:w-80">
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <p className="text-sm font-medium">Notifications</p>
            {unread > 0 ? (
              <button
                type="button"
                className="text-xs text-link hover:underline"
                onClick={() => markAll.mutate()}
              >
                Mark all read
              </button>
            ) : null}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {items.length === 0 ? (
              <p className="p-4 text-caption">No notifications yet.</p>
            ) : (
              items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={cn(
                    'block w-full border-b border-border px-3 py-2.5 text-left hover:bg-surface-secondary',
                    !item.isRead && 'bg-surface-secondary/50'
                  )}
                  onClick={() => void onOpenItem(item)}
                >
                  {item.meta ? (
                    <p className="text-2xs font-semibold uppercase tracking-wide text-content-tertiary">
                      {item.meta}
                    </p>
                  ) : null}
                  <p className="text-sm font-bold text-content">{item.title}</p>
                  <p className="text-caption mt-0.5 line-clamp-2">{item.message}</p>
                  {item.createdAt ? (
                    <p className="text-xs text-content-tertiary mt-1">
                      {new Date(item.createdAt).toLocaleString('en-IN')}
                    </p>
                  ) : null}
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

async function markAnnouncementReadQuiet(id: string): Promise<void> {
  const { markAnnouncementRead } = await import(
    'shared/data/repositories/announcementRepository'
  );
  await markAnnouncementRead(id);
}
